import { gql, useLazyQuery } from '@provision/graphql-client';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';

const GET_STRIPE_ACCOUNT_ONBOARD_URL = gql`
  query GetStripeAccountOnboardUrl($params: GetStripeAccountOnboardUrlInput!) {
    getStripeAccountOnboardUrl(params: $params) {
      url
    }
  }
`;

interface GetStripeAccountOnboardUrlResponse {
  getStripeAccountOnboardUrl: {
    url: string;
  };
}

interface GetStripeAccountOnboardUrlVariables {
  params: {
    partnerId: number;
    callbackUrl: string;
    country?: string;
  };
}

export const useStripeAccountOnboardUrl = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const [fetchOnboardUrl, { loading, error }] = useLazyQuery<
    GetStripeAccountOnboardUrlResponse,
    GetStripeAccountOnboardUrlVariables
  >(GET_STRIPE_ACCOUNT_ONBOARD_URL, {
    fetchPolicy: 'network-only',
    onError: (err) => {
      showErrorAlert(err.message || 'Failed to generate Stripe onboarding URL');
    },
  });

  const getOnboardUrl = useCallback(
    async (country?: string) => {
      if (isNullish(selectedPartnerId)) {
        showErrorAlert('Partner not selected');
        return null;
      }

      const callbackUrl = window.location.href;

      const result = await fetchOnboardUrl({
        variables: {
          params: {
            partnerId: selectedPartnerId,
            callbackUrl,
            country,
          },
        },
      });

      return result.data?.getStripeAccountOnboardUrl?.url ?? null;
    },
    [fetchOnboardUrl, selectedPartnerId, showErrorAlert],
  );

  const redirectToStripeOnboard = useCallback(
    async (country?: string) => {
      const url = await getOnboardUrl(country);
      if (url) {
        window.location.href = url;
      }
    },
    [getOnboardUrl],
  );

  return {
    getOnboardUrl,
    redirectToStripeOnboard,
    loading,
    error,
  };
};
